import React, { useState, useEffect, useMemo } from 'react';
import { Form, Input, DatePicker, InputNumber, Select, Button, message, Alert, Switch, Row, Col, Space, Tag, Spin } from 'antd';
import { useNavigate } from 'react-router-dom';
import { TournamentService } from '@/services/tournament.service';
import { CategoryService } from '@/services/category.service';
import { ClubService } from '@/services/club.service';
import apiService from '@/services/api.service';
import type { ApiResponse } from '@/types/api';
import { API_ENDPOINTS } from '@/constants/api.constants';
import type { CreateTournamentDTO } from '@/types/api.types';
import type { Category } from '@/types/models/tournament';
import type { Club } from '@/types/api.types';
import moment from 'moment';
import 'moment/locale/ru';
import { customLocale, datePickerProps } from '@/utils/datePickerUtils';
import { logger } from '@/utils/logger';
import { useAuth } from '@/contexts/AuthContext';

// Настраиваем moment для русской локализации
moment.locale('ru');

const { TextArea } = Input;
const { Option } = Select;

// Удаляем неиспользуемый enum TOURNAMENT_STATUS

export const CreateTournament: React.FC = () => {
    const [form] = Form.useForm();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [licenseCheckComplete, setLicenseCheckComplete] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [categories, setCategories] = useState<Category[]>([]);
    const [loadingCategories, setLoadingCategories] = useState(false);
    const [categoryError, setCategoryError] = useState<string | null>(null);
    const [clubs, setClubs] = useState<Club[]>([]);
    const [loadingClubs, setLoadingClubs] = useState(false);
    const [selectedCategories, setSelectedCategories] = useState<number[]>([]);
    const [searchText, setSearchText] = useState('');

    // Группируем категории по полу и фильтруем по поиску
    const categoriesByGender = useMemo(() => {
        let filtered = categories;
        
        if (searchText) {
            const searchLower = searchText.toLowerCase();
            filtered = categories.filter(c => 
                c.name.toLowerCase().includes(searchLower) ||
                `${c.ageFrom}-${c.ageTo}`.includes(searchLower) ||
                `${c.weightFrom}-${c.weightTo}`.includes(searchLower) ||
                (c.gender === 'male' && searchLower.includes('муж')) ||
                (c.gender === 'female' && searchLower.includes('жен'))
            );
        }
        
        const male = filtered.filter(c => c.gender === 'male');
        const female = filtered.filter(c => c.gender === 'female');
        return { male, female };
    }, [categories, searchText]);

    const onFinish = async (values: any) => {
        setSubmitting(true);
        try {
            const formattedValues: CreateTournamentDTO = {
                ...values,
                tournamentDate: values.tournamentDate.format('YYYY-MM-DD'),
                registrationDeadline: values.registrationDeadline.format('YYYY-MM-DD'),
                weighingDates: values.weighingDates?.map((date: moment.Moment) =>
                    date.format('YYYY-MM-DD')),
                categoryIds: values.categoryIds || [],
                autoRegistration: values.autoRegistration || false,
                isInviteOnly: values.isInviteOnly || false,
                hasTwoThirdPlaces: values.hasTwoThirdPlaces || false,
                disciplineType: values.disciplineType || undefined,
                victoryByPoints: values.disciplineType === 'kumite' && values.victoryByPoints != null ? values.victoryByPoints : undefined,
                invitedClubIds: values.isInviteOnly ? (values.invitedClubIds || []) : undefined
            };

            const tournament = await TournamentService.create(formattedValues);

            // Если выбраны категории, добавляем их к турниру
            if (values.categoryIds && values.categoryIds.length > 0) {
                try {
                    const categories = values.categoryIds.map((categoryId: number) => ({
                        tournamentId: tournament.id,
                        categoryId,
                        status: 'active' as const
                    }));

                    await TournamentService.updateCategories(tournament.id, categories);                    
                } catch (categoryError) {
                    logger.error('Error adding categories to tournament:', categoryError);
                    message.warning('Турнир создан, но возникла ошибка при добавлении категорий');
                }
            }

            message.success('Турнир успешно создан');
            navigate(`/tournaments/${tournament.id}`);
        } catch (error: any) {
            logger.error('Error creating tournament:', error);

            if (error.response?.status === 401) {
                message.error('Необходима авторизация');
                navigate('/login');
            } else if (
                error.response?.status === 403 &&
                (error.response?.data?.needExtendedLicensePayment === true ||
                    /расширенн.*лицензи/i.test(error.response?.data?.message || ''))
            ) {
                message.warning(error.response?.data?.message || 'Для создания турниров нужна расширенная лицензия.');
                navigate('/extended-license', {
                    state: {
                        fromCreate: true,
                        extendedLicensePrice: error.response?.data?.extendedLicensePrice ?? 0
                    }
                });
            } else if (error.response?.status === 400) {
                message.error(error.response.data.message || 'Проверьте правильность заполнения всех полей');
            } else if (error.response?.data?.message) {
                message.error(error.response.data.message);
            } else {
                message.error('Ошибка при создании турнира. Попробуйте позже.');
            }
        } finally {
            setSubmitting(false);
        }
    };

    // Проверка расширенной лицензии при заходе на страницу (прямая ссылка)
    useEffect(() => {
        if (user?.role === 'admin') {
            setLicenseCheckComplete(true);
            return;
        }
        if (!user) {
            setLicenseCheckComplete(true);
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const res = await apiService.get<ApiResponse<{ canCreateTournament: boolean; extendedLicensePrice?: number }>>(
                    API_ENDPOINTS.PAYMENTS.EXTENDED_LICENSE_INFO
                );
                const data = res.data?.data;
                if (cancelled) return;
                if (data && !data.canCreateTournament) {
                    navigate('/extended-license', { state: { fromCreate: true, extendedLicensePrice: data.extendedLicensePrice }, replace: true });
                    return;
                }
                setLicenseCheckComplete(true);
            } catch (e) {
                if (!cancelled) {
                    logger.error('Error checking extended license:', e);
                    setLicenseCheckComplete(true);
                }
            }
        })();
        return () => { cancelled = true; };
    }, [user?.role, navigate]);

    // Загрузка категорий при монтировании компонента
    useEffect(() => {
        const fetchCategories = async () => {
            setLoadingCategories(true);
            setCategoryError(null);
            try {                
                const data = await CategoryService.getAll();
                setCategories(data);
            } catch (error) {
                logger.error('Error loading categories:', error);
                setCategoryError('Не удалось загрузить категории');
            } finally {
                setLoadingCategories(false);
            }
        };

        fetchCategories();
    }, []);

    // Загрузка клубов при монтировании компонента
    useEffect(() => {
        const fetchClubs = async () => {
            setLoadingClubs(true);
            try {                
                const response = await ClubService.getClubs({ limit: 1000 });
                const clubsList = Array.isArray(response.data) ? response.data : response.data.rows || [];
                setClubs(clubsList);                
            } catch (error) {
                logger.error('Error loading clubs:', error);
                message.warning('Не удалось загрузить список клубов');
            } finally {
                setLoadingClubs(false);
            }
        };

        fetchClubs();
    }, []);

    if (!licenseCheckComplete) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
                <Spin size="large" />
            </div>
        );
    }

    return (
        <Form
            form={form}
            onFinish={onFinish}
            layout="vertical"
            disabled={submitting}
        >
            <Form.Item
                name="name"
                label="Название турнира"
                rules={[{ required: true, message: 'Введите название турнира' }]}
            >
                <Input />
            </Form.Item>

            <Form.Item
                name="location"
                label="Место проведения"
                rules={[{ required: true, message: 'Укажите место проведения' }]}
            >
                <Input />
            </Form.Item>

                <Form.Item
                    name="tournamentDate"
                    label="Дата проведения"
                    rules={[{ required: true, message: 'Выберите дату проведения' }]}
                >
                    <DatePicker 
                        {...datePickerProps}
                        locale={customLocale}
                        placeholder="Выберите дату (ДД.ММ.ГГГГ)"
                    />
                </Form.Item>

                <Form.Item
                    name="registrationDeadline"
                    label="Окончание регистрации"
                    rules={[{ required: true, message: 'Укажите окончание регистрации' }]}
                >
                    <DatePicker 
                        {...datePickerProps}
                        locale={customLocale}
                        placeholder="Выберите дату (ДД.ММ.ГГГГ)"
                    />
                </Form.Item>

            <Form.Item
                name="feeForClubMembers"
                label="Взнос для членов клуба"
                rules={[{ required: true, message: 'Укажите размер взноса' }]}
            >
                <InputNumber min={0} />
            </Form.Item>

            <Form.Item
                name="feeForOthers"
                label="Взнос для остальных участников"
                rules={[{ required: true, message: 'Укажите размер взноса' }]}
            >
                <InputNumber min={0} />
            </Form.Item>

            {/* Поле organizerId будет заполнено автоматически на бэкенде */}

            <Form.Item name="description" label="Описание">
                <TextArea rows={4} />
            </Form.Item>

            {/* Убрали поле beltLevels по требованию */}

            <Form.Item name="weighingDates" label="Даты взвешивания">
                <DatePicker.RangePicker 
                    format="DD.MM.YYYY" 
                    locale={customLocale}
                    placeholder={['Начало', 'Конец']}
                    allowEmpty={[true, true]}
                    style={{ width: '100%' }}
                    disabledDate={() => false}
                />
            </Form.Item>

            <Form.Item
                name="categoryIds"
                label="Категории"
                help="Выберите категории для турнира"
            >
                {categoryError && <Alert message={categoryError} type="error" style={{ marginBottom: 16 }} />}
                {loadingCategories ? (
                    <div style={{ textAlign: 'center', padding: '20px' }}>Загрузка категорий...</div>
                ) : (
                    <div style={{ 
                        border: '1px solid #d9d9d9', 
                        borderRadius: '6px', 
                        padding: '16px',
                        backgroundColor: '#fafafa'
                    }}>
                        <Space direction="vertical" style={{ width: '100%' }} size="small">
                            <Row gutter={8} align="middle">
                                <Col flex="auto">
                                    <Input
                                        placeholder="Поиск по названию, возрасту или весу..."
                                        value={searchText}
                                        onChange={(e) => setSearchText(e.target.value)}
                                        allowClear
                                        style={{ marginBottom: '8px' }}
                                    />
                                </Col>
                                <Col>
                                    <Button 
                                        type="link" 
                                        size="small"
                                        onClick={() => {
                                            const allIds = categories.map(c => c.id);
                                            setSelectedCategories(allIds);
                                            form.setFieldValue('categoryIds', allIds);
                                        }}
                                    >
                                        Все
                                    </Button>
                                </Col>
                                <Col>
                                    <Button 
                                        type="link" 
                                        size="small"
                                        onClick={() => {
                                            setSelectedCategories([]);
                                            form.setFieldValue('categoryIds', []);
                                        }}
                                    >
                                        Снять
                                    </Button>
                                </Col>
                            </Row>
                            
                            {selectedCategories.length > 0 && (
                                <div style={{ marginBottom: '8px' }}>
                                    <div style={{ fontSize: '12px', color: '#8c8c8c', marginBottom: '8px' }}>
                                        Выбрано: {selectedCategories.length}
                                    </div>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                        {selectedCategories.map(catId => {
                                            const category = categories.find(c => c.id === catId);
                                            if (!category) return null;
                                            return (
                                                <Tag
                                                    key={catId}
                                                    closable
                                                    onClose={() => {
                                                        const newSelected = selectedCategories.filter(id => id !== catId);
                                                        setSelectedCategories(newSelected);
                                                        form.setFieldValue('categoryIds', newSelected);
                                                    }}
                                                    color="blue"
                                                    style={{ margin: 0 }}
                                                >
                                                    {category.name}
                                                </Tag>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </Space>

                        <div style={{ maxHeight: '350px', overflowY: 'auto', marginTop: '12px' }}>
                            {categoriesByGender.male.length > 0 && (
                                <div style={{ marginBottom: '16px' }}>
                                    <div style={{ 
                                        fontWeight: 600, 
                                        fontSize: '14px', 
                                        marginBottom: '8px',
                                        color: '#1890ff'
                                    }}>
                                        Мужчины ({categoriesByGender.male.length})
                                    </div>
                                    <Row gutter={[8, 8]}>
                                        {categoriesByGender.male.map(category => {
                                            const isSelected = selectedCategories.includes(category.id);
                                            return (
                                                <Col xs={24} sm={12} md={8} lg={6} key={category.id}>
                                                    <div
                                                        onClick={() => {
                                                            const newSelected = isSelected
                                                                ? selectedCategories.filter(id => id !== category.id)
                                                                : [...selectedCategories, category.id];
                                                            setSelectedCategories(newSelected);
                                                            form.setFieldValue('categoryIds', newSelected);
                                                        }}
                                                        style={{
                                                            padding: '8px 12px',
                                                            border: `2px solid ${isSelected ? '#1890ff' : '#d9d9d9'}`,
                                                            borderRadius: '6px',
                                                            backgroundColor: isSelected ? '#e6f7ff' : '#fff',
                                                            cursor: 'pointer',
                                                            transition: 'all 0.2s',
                                                            fontSize: '13px'
                                                        }}
                                                        onMouseEnter={(e) => {
                                                            if (!isSelected) {
                                                                e.currentTarget.style.borderColor = '#40a9ff';
                                                                e.currentTarget.style.backgroundColor = '#f0f0f0';
                                                            }
                                                        }}
                                                        onMouseLeave={(e) => {
                                                            if (!isSelected) {
                                                                e.currentTarget.style.borderColor = '#d9d9d9';
                                                                e.currentTarget.style.backgroundColor = '#fff';
                                                            }
                                                        }}
                                                    >
                                                        <div style={{ fontWeight: 500, marginBottom: '4px' }}>
                                                            {category.name}
                                                        </div>
                                                        <div style={{ fontSize: '11px', color: '#8c8c8c' }}>
                                                            {category.ageFrom}-{category.ageTo} лет
                                                        </div>
                                                        <div style={{ fontSize: '11px', color: '#8c8c8c' }}>
                                                            {category.weightFrom}-{category.weightTo} кг
                                                        </div>
                                                    </div>
                                                </Col>
                                            );
                                        })}
                                    </Row>
                                </div>
                            )}

                            {categoriesByGender.female.length > 0 && (
                                <div>
                                    <div style={{ 
                                        fontWeight: 600, 
                                        fontSize: '14px', 
                                        marginBottom: '8px',
                                        marginTop: '16px',
                                        color: '#eb2f96'
                                    }}>
                                        Женщины ({categoriesByGender.female.length})
                                    </div>
                                    <Row gutter={[8, 8]}>
                                        {categoriesByGender.female.map(category => {
                                            const isSelected = selectedCategories.includes(category.id);
                                            return (
                                                <Col xs={24} sm={12} md={8} lg={6} key={category.id}>
                                                    <div
                                                        onClick={() => {
                                                            const newSelected = isSelected
                                                                ? selectedCategories.filter(id => id !== category.id)
                                                                : [...selectedCategories, category.id];
                                                            setSelectedCategories(newSelected);
                                                            form.setFieldValue('categoryIds', newSelected);
                                                        }}
                                                        style={{
                                                            padding: '8px 12px',
                                                            border: `2px solid ${isSelected ? '#eb2f96' : '#d9d9d9'}`,
                                                            borderRadius: '6px',
                                                            backgroundColor: isSelected ? '#fff0f6' : '#fff',
                                                            cursor: 'pointer',
                                                            transition: 'all 0.2s',
                                                            fontSize: '13px'
                                                        }}
                                                        onMouseEnter={(e) => {
                                                            if (!isSelected) {
                                                                e.currentTarget.style.borderColor = '#ff85c0';
                                                                e.currentTarget.style.backgroundColor = '#f0f0f0';
                                                            }
                                                        }}
                                                        onMouseLeave={(e) => {
                                                            if (!isSelected) {
                                                                e.currentTarget.style.borderColor = '#d9d9d9';
                                                                e.currentTarget.style.backgroundColor = '#fff';
                                                            }
                                                        }}
                                                    >
                                                        <div style={{ fontWeight: 500, marginBottom: '4px' }}>
                                                            {category.name}
                                                        </div>
                                                        <div style={{ fontSize: '11px', color: '#8c8c8c' }}>
                                                            {category.ageFrom}-{category.ageTo} лет
                                                        </div>
                                                        <div style={{ fontSize: '11px', color: '#8c8c8c' }}>
                                                            {category.weightFrom}-{category.weightTo} кг
                                                        </div>
                                                    </div>
                                                </Col>
                                            );
                                        })}
                                    </Row>
                                </div>
                            )}

                            {categories.length === 0 && !loadingCategories && (
                                <div style={{ textAlign: 'center', color: '#8c8c8c', padding: '20px' }}>
                                    Нет доступных категорий
                                </div>
                            )}

                            {searchText && categoriesByGender.male.length === 0 && categoriesByGender.female.length === 0 && (
                                <div style={{ textAlign: 'center', color: '#8c8c8c', padding: '20px' }}>
                                    Ничего не найдено
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </Form.Item>

            <Form.Item
                name="autoRegistration"
                label="Автоматическая регистрация"
                valuePropName="checked"
                help="Если включено, регистрация участников подтверждается автоматически без участия организатора"
            >
                <Switch />
            </Form.Item>

            <Form.Item
                name="isInviteOnly"
                label="Турнир по приглашениям"
                valuePropName="checked"
                help="Если включено, турнир будет виден только приглашенным клубам и организатору"
            >
                <Switch onChange={(checked) => {
                    if (!checked) {
                        form.setFieldsValue({ invitedClubIds: undefined });
                    }
                }} />
            </Form.Item>

            <Form.Item
                name="hasTwoThirdPlaces"
                label="Два третьих места"
                valuePropName="checked"
                help="Если включено, количество боев = количество участников - 1 (два 3 места), иначе количество боев = количество участников"
            >
                <Switch />
            </Form.Item>

            <Form.Item
                name="disciplineType"
                label="Дисциплина"
                help="Кумите — поединки по очкам, Ката — выполнение формальных комплексов"
            >
                <Select placeholder="Выберите тип дисциплины" allowClear>
                    <Option value="kumite">Кумите</Option>
                    <Option value="kata">Ката</Option>
                </Select>
            </Form.Item>

            <Form.Item
                noStyle
                shouldUpdate={(prevValues, currentValues) => prevValues.disciplineType !== currentValues.disciplineType}
            >
                {({ getFieldValue }) =>
                    getFieldValue('disciplineType') === 'kumite' ? (
                        <Form.Item
                            name="victoryByPoints"
                            label="Победа по очкам"
                            help="Количество очков для досрочной победы в поединке (например, 8)"
                            rules={[
                                {
                                    required: true,
                                    message: 'Укажите количество очков для победы'
                                }
                            ]}
                        >
                            <InputNumber min={1} max={15} style={{ width: '100%' }} placeholder="Например, 8" />
                        </Form.Item>
                    ) : null
                }
            </Form.Item>

            <Form.Item
                noStyle
                shouldUpdate={(prevValues, currentValues) => prevValues.isInviteOnly !== currentValues.isInviteOnly}
            >
                {({ getFieldValue }) =>
                    getFieldValue('isInviteOnly') ? (
                        <Form.Item
                            name="invitedClubIds"
                            label="Пригласить клубы"
                            rules={[
                                {
                                    required: true,
                                    message: 'Выберите хотя бы один клуб для приглашения'
                                }
                            ]}
                            help="Выберите клубы, которые будут приглашены на турнир"
                        >
                            <Select
                                mode="multiple"
                                placeholder="Выберите клубы"
                                loading={loadingClubs}
                                style={{ width: '100%' }}
                                optionFilterProp="children"
                                showSearch
                                filterOption={(input, option) => {
                                    const label = typeof option?.children === 'string' 
                                        ? option.children 
                                        : String(option?.children || '');
                                    return label.toLowerCase().includes(input.toLowerCase());
                                }}
                            >
                                {clubs.map(club => (
                                    <Option key={club.id} value={club.id}>
                                        {club.name} {club.city ? `(${club.city})` : ''}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                    ) : null
                }
            </Form.Item>

            <Form.Item>
                <Button type="primary" htmlType="submit" loading={submitting}>
                    Создать турнир
                </Button>
            </Form.Item>
        </Form>
    );
};

export default CreateTournament;